/* The admin portal.
 *
 * Built 2026-09-17 from `Damga Admin Portal v2.dc.html`, against the reading in
 * reports/surfaces/admin-portal.md.
 *
 * WHO CAN OPEN IT. Damga's own staff, by the same fail-closed email allowlist
 * that guards the leads. The gate is the API's, not this app's: every route
 * refuses a caller who is not on the list, and this screen shows the refusal
 * rather than deciding anything itself. An unconfigured deployment shows
 * nobody anything.
 *
 * NO WRITES, AND NO IMPERSONATION. Every route here is a GET. The artboard's
 * primary action on most screens is a write -- onboard, suspend, refund, ship,
 * impersonate -- and none of them exists. Impersonation is the one this build
 * refuses rather than defers: an account that can sign in as a merchant can
 * read every shop's customers, which is exactly what the tenant boundary is
 * for. The Support screen says so in those words.
 *
 * ONE LOAD, NOT ONE PER SCREEN, because the portal is small and the counts are
 * cheap; the leads are fetched only when their screen is opened, because
 * opening them writes a row to the audit log and an idle tab should not.
 */
import {
  Forbidden,
  Offline,
  SessionExpired,
  type Session,
  type OAuthProvider,
  beginOAuth,
  completeOAuth,
  createAuth,
  createClient,
  enabledProviders,
  isRefusal,
  looksLikeOAuthReturn,
  readSession,
  writeSession,
} from "@damga/kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { t, type Key } from "../content";
import { API_URL, AUTH_URL } from "./config";
import {
  AuditScreen,
  ComplianceScreen,
  FlagsScreen,
  InfraScreen,
  MerchantsScreen,
  Nothing,
  OutboxScreen,
  OverviewScreen,
  PipelineScreen,
  ProvisionScreen,
  StaffScreen,
  type OutboxMessage,
  type ProvisionInput,
  type Compliance,
  type Flag,
  type Action,
  moment,
  type Lead,
  type Merchant,
  type Overview,
  type View,
} from "./screens";
import { SignIn } from "./SignIn";

type Tab =
  | "overview" | "revenue" | "finance" | "health" | "infra" | "settings"
  | "merchants" | "merchant" | "pipeline" | "provision"
  | "consumers" | "consumer" | "app"
  | "fraud" | "compliance" | "content" | "audit"
  | "support" | "messaging" | "integrations" | "flags" | "staff" | "outbox";

/** The artboard's own five groups, in its own order. */
const GROUPS: { label: Key; items: { key: Tab; label: Key }[] }[] = [
  {
    label: "group.platform",
    items: [
      { key: "overview", label: "nav.overview" },
      { key: "revenue", label: "nav.revenue" },
      { key: "finance", label: "nav.finance" },
      { key: "health", label: "nav.health" },
      { key: "infra", label: "nav.infra" },
      { key: "settings", label: "nav.settings" },
    ],
  },
  {
    label: "group.merchants",
    items: [
      { key: "merchants", label: "nav.merchants" },
      { key: "merchant", label: "nav.merchant" },
      { key: "pipeline", label: "nav.pipeline" },
      // 2026-09-18: Damga issues the merchant's sign-in, and this is where.
      { key: "provision", label: "nav.provision" },
    ],
  },
  {
    label: "group.consumers",
    items: [
      { key: "consumers", label: "nav.consumers" },
      { key: "consumer", label: "nav.consumer" },
      { key: "app", label: "nav.app" },
    ],
  },
  {
    label: "group.trust",
    items: [
      { key: "fraud", label: "nav.fraud" },
      { key: "compliance", label: "nav.compliance" },
      { key: "content", label: "nav.content" },
      { key: "audit", label: "nav.audit" },
    ],
  },
  {
    label: "group.operations",
    items: [
      { key: "support", label: "nav.support" },
      { key: "messaging", label: "nav.messaging" },
      { key: "integrations", label: "nav.integrations" },
      { key: "flags", label: "nav.flags" },
      { key: "staff", label: "nav.staff" },
      // Staging only: shown when the API has an outbox, which Doha never does.
      { key: "outbox", label: "nav.outbox" },
    ],
  },
];

const TITLES: Record<Tab, Key> = {
  overview: "overview.title",
  revenue: "revenue.title",
  finance: "finance.title",
  health: "health.title",
  infra: "infra.title",
  settings: "settings.title",
  merchants: "merchants.title",
  merchant: "merchant.title",
  pipeline: "pipeline.title",
  provision: "provision.title",
  consumers: "consumers.title",
  consumer: "consumersDetail.title",
  app: "app.title",
  fraud: "fraud.title",
  compliance: "compliance.title",
  content: "content.title",
  audit: "audit.title",
  support: "support.title",
  messaging: "messaging.title",
  integrations: "integrations.title",
  flags: "flags.title",
  staff: "staff.title",
  outbox: "outbox.title",
};

export function App() {
  const [session, setSessionState] = useState<Session | null>(() => readSession<Session>());
  const [returning, setReturning] = useState(() =>
    typeof window === "undefined" ? false : looksLikeOAuthReturn(window.location),
  );
  const [providers, setProviders] = useState<Set<OAuthProvider> | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [problem, setProblem] = useState<"offline" | "server" | "notStaff" | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  // A section opens at its own top. Choosing Outbox from low in a long sidebar
  // used to leave the page scrolled, with the section's heading out of view
  // (review of 2026-09-18, finding 23).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);
  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState(false);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [merchants, setMerchants] = useState<Merchant[] | null>(null);
  const [compliance, setCompliance] = useState<Compliance | null>(null);
  const [views, setViews] = useState<View[] | null>(null);
  const [actions, setActions] = useState<Action[] | null>(null);
  const [flags, setFlags] = useState<Flag[] | null>(null);
  const [leads, setLeads] = useState<Lead[] | null>(null);
  // Null where there is no outbox (Doha: the route answers 404), and the screen is
  // then not offered at all.
  const [outbox, setOutbox] = useState<OutboxMessage[] | null>(null);

  const setSession = useCallback((s: Session | null) => {
    writeSession(s);
    setSessionState(s);
  }, []);
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const api = useMemo(
    () => createClient({ apiUrl: API_URL, authUrl: AUTH_URL, getSession: () => sessionRef.current, setSession }),
    [setSession],
  );
  const auth = useMemo(() => createAuth(AUTH_URL), []);

  useEffect(() => {
    if (session) return;
    let live = true;
    void enabledProviders(AUTH_URL)
      .then((s) => live && setProviders(s))
      .catch(() => live && setProviders(new Set()));
    return () => {
      live = false;
    };
  }, [session]);

  useEffect(() => {
    if (!returning) return;
    let live = true;
    void (async () => {
      try {
        const r = await completeOAuth(AUTH_URL);
        if (!live) return;
        if (r?.kind === "session") setSession(r.session);
        else if (r?.kind === "error") setAuthError(t("auth.unreachable"));
      } finally {
        if (live) setReturning(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [returning, setSession]);

  const load = useCallback(async () => {
    if (!sessionRef.current) return;
    try {
      const [ov, me, co, au, fl] = await Promise.all([
        api.call<Overview>("/staff/overview"),
        api.call<{ merchants: Merchant[] }>("/staff/merchants"),
        api.call<Compliance>("/staff/compliance"),
        api.call<{ views: View[]; actions: Action[] }>("/staff/audit"),
        api.call<{ flags: Flag[] }>("/staff/flags"),
      ]);
      setAsked(true);
      if (isRefusal(ov)) {
        // Signed in, and not on the list. A real state for anybody else's
        // account, and not an error: the screen says what the list is.
        setProblem("notStaff");
        return;
      }
      setProblem(null);
      setOverview(ov as unknown as Overview);
      if (!isRefusal(me)) setMerchants((me as { merchants: Merchant[] }).merchants);
      if (!isRefusal(co)) setCompliance(co as unknown as Compliance);
      if (!isRefusal(au)) {
        setViews((au as { views: View[] }).views);
        setActions((au as { actions?: Action[] }).actions ?? []);
      }
      if (!isRefusal(fl)) setFlags((fl as { flags: Flag[] }).flags);
      try {
        const ob = await api.call<{ messages: OutboxMessage[] }>("/staff/outbox");
        setOutbox(isRefusal(ob) ? null : (ob as { messages: OutboxMessage[] }).messages);
      } catch {
        setOutbox(null);
      }
    } catch (err) {
      setAsked(true);
      if (err instanceof SessionExpired) setSession(null);
      else if (err instanceof Offline) setProblem("offline");
      // Signed in, and not on the list. The API answers 403 E_NOT_STAFF, which
      // is a real state for anybody else's account and not a fault: the screen
      // says what the list is and who sets it.
      else if (err instanceof Forbidden) setProblem("notStaff");
      else setProblem("server");
    }
  }, [api, setSession]);

  /* EVERY SECTION OPENS ON WHAT IS TRUE NOW (2026-09-18). The portal read
   * everything once, when it opened, and refreshed only after setting up a shop.
   * Exercised in staging: two links sent, and the Audit log and the Outbox, opened
   * in the same session, showed neither. A record that is right only after a
   * reload is wrong on the screen where it matters. So each section re-reads on
   * opening. (Not the leads, below: reading them is itself logged.) */
  useEffect(() => {
    void load();
  }, [load, session, tab]);

  /* The leads are fetched when their screen is opened, and not before: the
   * route writes a row to the audit log, and a tab left open on Overview has
   * not looked at anybody's details. */
  useEffect(() => {
    if (tab !== "pipeline" || leads !== null || problem === "notStaff" || !session) return;
    let live = true;
    void (async () => {
      try {
        const r = await api.call<{ leads: Lead[] }>("/staff/leads");
        if (live && !isRefusal(r)) setLeads((r as { leads: Lead[] }).leads);
      } catch {
        if (live) setLeads([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [api, tab, leads, problem, session]);

  /* Setting up a shop. The answer is the sentence the screen shows. */
  const provision = useCallback(
    async (input: ProvisionInput): Promise<{ ok: boolean; message: string }> => {
      try {
        const r = await api.call<Record<string, unknown>>("/staff/merchants/provision", { method: "POST", body: input });
        if (isRefusal(r)) {
          const refusal = r as { refusal: string; params?: { reason?: string } };
          if (refusal.refusal === "E_MAIL_NOT_SENT") return { ok: false, message: t("provision.mailDown") };
          return { ok: false, message: t("provision.problem", { reason: refusal.params?.reason ?? t("shell.problem") }) };
        }
        void load();
        const out = r as { owner_already_signed_in?: boolean };
        return {
          ok: true,
          message: out.owner_already_signed_in
            ? t("provision.already", { name: input.name })
            : t("provision.done", { name: input.name, email: input.owner_email }),
        };
      } catch {
        return { ok: false, message: t("provision.problem", { reason: t("shell.problem") }) };
      }
    },
    [api, load],
  );

  const ownerLink = useCallback(
    async (businessId: string): Promise<string> => {
      try {
        const r = await api.call<Record<string, unknown>>("/staff/merchants/owner-link", {
          method: "POST",
          body: { business_id: businessId },
        });
        if (!isRefusal(r)) return t("merchants.ownerLinkSent");
        const refusal = r as { refusal?: string; params?: { after?: string } };
        // THREE A DAY (050): said with the time the next one can go, in words.
        if (refusal.refusal === "E_TOO_MANY_LINKS") return t("merchants.ownerLinkTooMany", { at: moment(refusal.params?.after ?? null) });
        if (refusal.refusal === "E_MAIL_NOT_SENT") return t("merchants.ownerLinkMailDown");
        return t("merchants.ownerLinkProblem");
      } catch {
        return t("merchants.ownerLinkProblem");
      }
    },
    [api],
  );

  const goToProvider = useCallback(async (provider: OAuthProvider) => {
    setAuthError(null);
    try {
      await beginOAuth(AUTH_URL, provider, window.location.origin + window.location.pathname);
    } catch {
      setAuthError(t("auth.unreachable"));
    }
  }, []);

  if (!session) {
    if (returning) {
      return (
        <div className="signin">
          <p className="note">{t("shell.loading")}</p>
        </div>
      );
    }
    return (
      <SignIn
        auth={auth}
        providers={providers}
        error={authError}
        onProvider={(p) => void goToProvider(p)}
        onSignedIn={setSession}
      />
    );
  }

  /* A REFUSED ACCOUNT GETS A SCREEN, NOT THE SHELL. Showing the sidebar to
   * somebody who cannot open any of it advertises the portal's structure and
   * invites twenty-one clicks that all say the same thing. */
  if (problem === "notStaff") {
    return (
      <div className="signin">
        <div className="signin-card">
          <div className="side-brand" style={{ padding: 0 }}>
            <span className="mark" aria-hidden="true" />
            <span className="side-word">{t("shell.title")}</span>
          </div>
          <div>
            <h1 className="title">{t("auth.notStaff")}</h1>
            <p className="lead">{t("auth.notStaffBody")}</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => setSession(null)}>
            {t("shell.signOut")}
          </button>
        </div>
      </div>
    );
  }

  const body = () => {
    switch (tab) {
      // --- the eight with something behind them ---
      case "overview":
        return !asked || !overview ? <p className="note">{t("shell.loading")}</p> : <OverviewScreen data={overview} />;
      case "merchants":
        return <MerchantsScreen merchants={merchants ?? []} query={query} onQuery={setQuery} onOwnerLink={ownerLink} />;
      case "provision":
        return <ProvisionScreen onProvision={provision} />;
      case "outbox":
        return <OutboxScreen messages={outbox ?? []} />;
      case "pipeline":
        return <PipelineScreen leads={leads ?? []} />;
      case "compliance":
        return <ComplianceScreen data={compliance ?? { erasures: [], consent_versions: [] }} />;
      case "audit":
        return <AuditScreen views={views ?? []} actions={actions ?? []} />;
      case "fraud":
        return <FlagsScreen flags={flags ?? []} />;
      case "infra":
        return <InfraScreen />;
      case "staff":
        return <StaffScreen />;

      // --- the thirteen with nothing behind them ---
      case "revenue":
        return <Nothing title="revenue.empty" body="revenue.emptyBody" />;
      case "finance":
        return <Nothing title="finance.empty" body="finance.emptyBody" />;
      case "health":
        return <Nothing title="health.empty" body="health.emptyBody" />;
      case "settings":
        return <Nothing title="settings.empty" body="settings.emptyBody" />;
      case "merchant":
        return <Nothing title="merchant.empty" body="merchant.emptyBody" />;
      case "consumers":
      case "consumer":
        return <Nothing title="consumers.empty" body="consumers.emptyBody" />;
      case "app":
        return <Nothing title="app.empty" body="app.emptyBody" />;
      case "content":
        return <Nothing title="content.empty" body="content.emptyBody" />;
      case "support":
        return <Nothing title="support.empty" body="support.emptyBody" />;
      case "messaging":
        return <Nothing title="messaging.empty" body="messaging.emptyBody" />;
      case "integrations":
        return <Nothing title="integrations.empty" body="integrations.emptyBody" />;
      case "flags":
        return <Nothing title="flags.empty" body="flags.emptyBody" />;
    }
  };

  return (
    <div className="app">
      {/* THE SIDEBAR, AS THE DASHBOARD'S (review of 2026-09-18, findings 22-24).
          On a desktop it holds still and its sections scroll inside it: at
          1440 x 900 it was taller than the screen, so Outbox sat below the fold.
          Under 1000px it is one bar and a Menu, not a sideways row that cut
          "Onboarding" to "Onbo" with the group names inline. */}
      <aside className="side">
        <div className="side-top">
          <div className="side-brand">
            <span className="mark" aria-hidden="true" />
            <span className="side-word">{t("shell.title")}</span>
          </div>
          <button
            type="button"
            className="side-menu"
            aria-expanded={menuOpen}
            aria-controls="side-panel"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? t("shell.menuClose") : t("shell.menu")}
          </button>
        </div>
        <div id="side-panel" className="side-panel" data-open={menuOpen ? "" : undefined}>
          <nav className="side-nav" aria-label="Sections">
            {GROUPS.map((g) => (
              <div className="side-group" key={g.label}>
                <p className="side-group-label">{t(g.label)}</p>
                {g.items.filter((x) => x.key !== "outbox" || outbox !== null).map((x) => (
                  <button
                    key={x.key}
                    type="button"
                    className="side-link"
                    aria-current={tab === x.key ? "page" : undefined}
                    onClick={() => {
                      setTab(x.key);
                      setMenuOpen(false);
                    }}
                  >
                    {t(x.label)}
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div className="side-foot">
            <span className="side-who">{t("shell.readOnly")}</span>
            <button type="button" className="btn btn-quiet" onClick={() => setSession(null)}>
              {t("shell.signOut")}
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <div className="head">
          <div>
            <p className="eyebrow">{t("shell.title")}</p>
            <h1 className="title">{t(TITLES[tab])}</h1>
          </div>
        </div>

        {problem === "offline" && <p className="banner">{t("shell.offline")}</p>}
        {problem === "server" && <p className="banner">{t("shell.problem")}</p>}

        {body()}
      </main>
    </div>
  );
}
